import { format } from "date-fns";
import { useMemo } from "react";

import { type ResourceConfig, ResourcePage } from "@/components/resource-page";
import type { Translate } from "@/constants/i18n";
import { useTranslation } from "@/store/locale";

interface Change {
  field: string;
  old_value: string | null;
  new_value: string | null;
}

interface LogEntry {
  id: number;
  member_id: number;
  member_number: string;
  change_data: Change[];
  created_at: string;
  created_by: string;
  club_location: string;
}

const dash = (value: string | null) => value ?? "—";

/** One box per change, stacked, so the three change columns line up row for row. */
function Stack({ entry, pick }: { entry: LogEntry; pick: (change: Change) => string | null }) {
  return (
    <span className="grid gap-2">
      {entry.change_data.map((change, index) => (
        <span
          key={`${change.field}-${index}`}
          className="border-line bg-surface-sunken text-fg text-callout block truncate rounded-lg border px-3 py-2"
        >
          {dash(pick(change))}
        </span>
      ))}
    </span>
  );
}

const joined = (entry: LogEntry, pick: (change: Change) => string | null) =>
  entry.change_data.map((change) => dash(pick(change))).join(" | ");

const build = (t: Translate): ResourceConfig<LogEntry> => ({
  title: t("page.memberActivityLog"),
  singular: t("log.entry"),
  endpoint: "/member-logs",
  backTo: "/members",
  readOnly: true,
  exportable: true,
  // Every change ever made: it scrolls back through time, and nobody counts it.
  infinite: true,
  searchPlaceholder: t("log.memberNumberSearch"),
  columns: [
    {
      key: "member_number",
      label: t("log.memberAccountNumber"),
      render: (r) => r.member_number,
    },
    {
      key: "field",
      label: t("log.modification"),
      className: "min-w-56",
      render: (r) => <Stack entry={r} pick={(c) => c.field} />,
      csv: (r) => joined(r, (c) => c.field),
    },
    {
      key: "from",
      label: t("log.from"),
      className: "min-w-56",
      render: (r) => <Stack entry={r} pick={(c) => c.old_value} />,
      csv: (r) => joined(r, (c) => c.old_value),
    },
    {
      key: "to",
      label: t("log.to"),
      className: "min-w-56",
      render: (r) => <Stack entry={r} pick={(c) => c.new_value} />,
      csv: (r) => joined(r, (c) => c.new_value),
    },
    {
      key: "created_at",
      label: t("log.modifiedAt"),
      render: (r) => format(new Date(r.created_at), "MMM d, yyyy, h:mm a"),
    },
    { key: "created_by", label: t("log.modifiedBy"), render: (r) => r.created_by },
    { key: "club_location", label: t("log.clubLocation"), render: (r) => r.club_location },
  ],
  filters: [
    { name: "field", label: t("log.modification"), type: "text" },
    { name: "created_by", label: t("log.modifiedBy"), type: "text" },
    { name: "club_location", label: t("log.clubLocation"), type: "text" },
    { name: "date_from", label: t("log.fromDate"), type: "date" },
    { name: "date_to", label: t("log.toDate"), type: "date" },
  ],
  fields: [],
  toForm: () => ({}),
});

function MemberActivityLogPage() {
  const t = useTranslation();
  const config = useMemo(() => build(t), [t]);
  return <ResourcePage config={config} />;
}

export default MemberActivityLogPage;
